/**
 * Pricing rules for the metered plans.
 *
 * Both plans use a *marginal* scale, like income tax: every slice of the
 * quantity is billed at its own rate. A flat per-tier rate would make the bill
 * drop as the customer grows (80 communities cost more than 81), and would let
 * the largest territories reach absurd totals.
 */

export type NetworkType = "asso" | "pro";
export type SignatureType = "coll" | "fede" | "ent";

interface Bracket {
  /** Inclusive upper bound of the bracket. */
  upTo: number;
  rate: number;
}

/** Yearly price per community, associative rate. Pro doubles it. */
const NETWORK_BRACKETS: Bracket[] = [
  { upTo: 80, rate: 40 },
  { upTo: 150, rate: 35 },
  { upTo: 300, rate: 30 },
  { upTo: 500, rate: 25 },
  { upTo: Infinity, rate: 20 },
];

/** Yearly price per inhabitant/user, public-sector rate. Companies double it. */
const SIGNATURE_BRACKETS: Bracket[] = [
  { upTo: 50_000, rate: 0.1 },
  { upTo: 200_000, rate: 0.04 },
  { upTo: 500_000, rate: 0.015 },
  { upTo: 2_000_000, rate: 0.006 },
  { upTo: Infinity, rate: 0.002 },
];

export const NETWORK_MIN_COMMUNITIES = 1;
export const NETWORK_MAX_COMMUNITIES = 2_000;
export const SIGNATURE_MIN_UNITS = 0;
/** Large enough for the biggest metropolitan areas. */
export const SIGNATURE_MAX_UNITS = 30_000_000;
export const SIGNATURE_UNITS_STEP = 500;

export const NETWORK_DEFAULT_COMMUNITIES = 25;
export const SIGNATURE_DEFAULT_UNITS = 25_000;

/** Bracket rates in display order, so the discount grid stays in sync. */
export const networkBracketRates = (type: NetworkType) =>
  NETWORK_BRACKETS.map(({ rate }) => (type === "pro" ? rate * 2 : rate));

export const signatureBracketRates = (type: SignatureType) =>
  SIGNATURE_BRACKETS.map(({ rate }) => (type === "ent" ? rate * 2 : rate));

const meteredTotal = (brackets: Bracket[], quantity: number) => {
  let total = 0;
  let floor = 0;

  for (const bracket of brackets) {
    if (quantity <= floor) break;
    total += (Math.min(quantity, bracket.upTo) - floor) * bracket.rate;
    floor = bracket.upTo;
  }

  // Rates such as 0.015 make the running sum drift; cents are the finest
  // granularity the UI ever shows
  return Math.round(total * 100) / 100;
};

export const networkTotal = (communities: number, type: NetworkType) =>
  meteredTotal(NETWORK_BRACKETS, communities) * (type === "pro" ? 2 : 1);

/** What the customer effectively pays per community, all brackets blended. */
export const networkAverageRate = (communities: number, type: NetworkType) =>
  communities > 0
    ? networkTotal(communities, type) / communities
    : networkBracketRates(type)[0];

/** Flat yearly platform fee, before the metered part. */
export const signatureBase = (type: SignatureType) =>
  type === "fede" ? 1_500 : 3_000;

export const signatureMetered = (units: number, type: SignatureType) =>
  meteredTotal(SIGNATURE_BRACKETS, units) * (type === "ent" ? 2 : 1);

export const signatureTotal = (units: number, type: SignatureType) =>
  signatureBase(type) + signatureMetered(units, type);

/** Blended rate per inhabitant/user, excluding the flat base fee. */
export const signatureAverageRate = (units: number, type: SignatureType) =>
  units > 0
    ? signatureMetered(units, type) / units
    : signatureBracketRates(type)[0];

export const clampQuantity = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, Math.round(Number.isFinite(value) ? value : min)));

const euro = (locale: string, fractionDigits: number) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Totals and whole-euro bracket rates. */
export const formatEuro = (value: number, locale: string) =>
  euro(locale, 0).format(value);

/** Blended price per community. */
export const formatEuroCents = (value: number, locale: string) =>
  euro(locale, 2).format(value);

/** Per-inhabitant rates, which go down to fractions of a cent. */
export const formatEuroRate = (value: number, locale: string) =>
  euro(locale, 3).format(value);
